"use client";

import { useCallback, useState } from "react";
import Image from "next/image";

import { cn } from "@rivval/css";
import { DotPattern } from "@rivval/icons";
import { GlitchText } from "@rivval/ui/glitch-text";

import { env } from "~/env.mjs";
import { PLACEHOLDER_IMAGE } from "~/lib/config/content";

interface FeaturedTabContentData {
  id: string;
  title: string;
  content: string;
  images: string[];
}

interface FeaturedTabContentProps {
  featuredTabData: FeaturedTabContentData[];
  featuredTabTitle: string;
}

interface TabsSectionProps {
  featuredTabTitle: string;
  featuredTabData: FeaturedTabContentData[];
  activeTab: string;
  handleTabClick: (tabId: string) => void;
}

interface TabButtonProps {
  tab: FeaturedTabContentData;
  isActive: boolean;
  onClick: () => void;
}

interface MainContentProps {
  activeTabData: FeaturedTabContentData | undefined;
  selectedImageIndex: number;
  handleImageClick: (index: number) => void;
}

interface MainImageProps {
  src: string | undefined;
  alt: string;
}

interface TextOverlayProps {
  content: string;
}

interface ThumbnailsProps {
  images: string[];
  selectedIndex: number;
  onImageClick: (index: number) => void;
}

interface ThumbnailButtonProps {
  image: string;
  index: number;
  isActive: boolean;
  onClick: () => void;
}

interface OverlayProps {
  isActive: boolean;
}

interface ImageLabelProps {
  index: number;
}

export function FeaturedTabContent({
  featuredTabData,
  featuredTabTitle,
}: FeaturedTabContentProps) {
  const [activeTab, setActiveTab] = useState<string>(
    featuredTabData[0]?.id ?? "",
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

  const handleTabClick = useCallback((tabId: string) => {
    setActiveTab(tabId);
    setSelectedImageIndex(0);
  }, []);

  const handleImageClick = useCallback((index: number) => {
    setSelectedImageIndex(index);
  }, []);

  if (!featuredTabData.length) {
    return null;
  }

  const activeTabData = featuredTabData.find((tab) => tab.id === activeTab);

  return (
    <section className="razor-top relative bg-black py-12">
      <BackgroundPattern />
      <div className="container relative z-10 mx-auto flex flex-col gap-8 px-10 pb-24 lg:flex-row">
        <TabsSection
          featuredTabTitle={featuredTabTitle}
          featuredTabData={featuredTabData}
          activeTab={activeTab}
          handleTabClick={handleTabClick}
        />
        <MainContent
          activeTabData={activeTabData}
          selectedImageIndex={selectedImageIndex}
          handleImageClick={handleImageClick}
        />
      </div>
      <SectionModuleText />
    </section>
  );
}

function BackgroundPattern() {
  return (
    <div className="absolute inset-0 h-full w-full overflow-hidden">
      <DotPattern className="h-full w-full" variant="destructive" />
    </div>
  );
}

function TabsSection({
  featuredTabTitle,
  featuredTabData,
  activeTab,
  handleTabClick,
}: TabsSectionProps) {
  return (
    <div className="flex w-full flex-col justify-end lg:w-1/4">
      <h2 className="mb-4 text-4xl font-bold text-yellow-400 lg:mb-8">
        <GlitchText text={featuredTabTitle} glitchDuration={1000} />
      </h2>
      <div className="flex flex-row space-x-1 lg:flex-col lg:space-x-0 lg:space-y-1">
        {featuredTabData.map((tab) => (
          <TabButton
            key={tab.id}
            tab={tab}
            isActive={activeTab === tab.id}
            onClick={() => handleTabClick(tab.id)}
          />
        ))}
      </div>
    </div>
  );
}

function TabButton({ tab, isActive, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "group relative px-6 py-4 text-left text-lg font-medium transition-all duration-200",
        "border-b-2 hover:bg-black/20 hover:text-yellow-400 lg:border-l-2",
        isActive
          ? "border-yellow-400 bg-black/30 text-yellow-400"
          : "border-transparent text-gray-400",
      )}
    >
      <span className="inline-block transform uppercase transition-transform duration-200 group-hover:translate-x-1">
        {tab.title}
      </span>
    </button>
  );
}

function MainContent({
  activeTabData,
  selectedImageIndex,
  handleImageClick,
}: MainContentProps) {
  if (!activeTabData) return null;

  return (
    <div className="relative w-full lg:w-3/4">
      <div className="relative">
        <MainImage
          src={activeTabData.images[selectedImageIndex]}
          alt={activeTabData.title}
        />
        <TextOverlay content={activeTabData.content} />
        <Thumbnails
          images={activeTabData.images}
          selectedIndex={selectedImageIndex}
          onImageClick={handleImageClick}
        />
      </div>
    </div>
  );
}

function MainImage({ src, alt }: MainImageProps) {
  return (
    <div className="relative aspect-video bg-black/50">
      <Image
        src={`${env.NEXT_PUBLIC_ASSETS_URL}${src ?? PLACEHOLDER_IMAGE}`}
        alt={alt}
        className="h-full w-full object-cover"
        fill
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      />
    </div>
  );
}

function TextOverlay({ content }: TextOverlayProps) {
  return (
    <div className="absolute bottom-0 right-0 top-0 hidden w-full flex-col justify-end bg-linear-to-l from-black via-black/95 to-transparent p-8 md:flex lg:w-1/2">
      <p className="text-lg text-white">{content}</p>
      <div className="mt-4 font-mono text-sm text-gray-400">
        <p>AUTHORIZED BY: ████████</p>
        <p>DATE: ██/██/████</p>
      </div>
    </div>
  );
}

function Thumbnails({ images, selectedIndex, onImageClick }: ThumbnailsProps) {
  return (
    <div className="lg:-bottom-30 absolute -bottom-12 left-0 right-0 px-4 sm:-bottom-16 md:-bottom-20">
      <div className="flex justify-center space-x-2 sm:space-x-3 md:space-x-4">
        {images.map((image, index) => (
          <ThumbnailButton
            key={index}
            image={`${env.NEXT_PUBLIC_ASSETS_URL}${image}`}
            index={index}
            isActive={selectedIndex === index}
            onClick={() => onImageClick(index)}
          />
        ))}
      </div>
    </div>
  );
}

function ThumbnailButton({
  image,
  index,
  isActive,
  onClick,
}: ThumbnailButtonProps) {
  return (
    <button
      onClick={onClick}
      className="group relative aspect-video w-20 shrink-0 overflow-hidden sm:w-28 md:w-36 lg:w-48"
    >
      <Image
        src={image}
        alt={`Thumbnail ${index + 1}`}
        className={cn(
          "h-full w-full object-cover transition-all duration-300",
          !isActive && "scale-110 blur-xs brightness-50 filter",
        )}
        fill
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      />
      <PixelatedOverlay isActive={isActive} />
      <BorderOverlay isActive={isActive} />
      <ImageLabel index={index} />
    </button>
  );
}

function PixelatedOverlay({ isActive }: OverlayProps) {
  return (
    <div
      className={cn(
        "absolute inset-0 bg-cover bg-center transition-opacity duration-300",
        "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 viewBox=%220 0 8 8%22%3E%3Cpath d=%22M4 0h4v4H4V0zM0 4h4v4H0V4z%22 fill=%22%23fff%22%2F%3E%3C%2Fsvg%3E')]",
        "bg-[length:8px_8px]",
        isActive ? "opacity-0" : "opacity-50",
      )}
    />
  );
}

function BorderOverlay({ isActive }: OverlayProps) {
  return (
    <div
      className={cn(
        "absolute inset-0 transition-colors duration-300",
        "border-4",
        isActive ? "border-yellow-400" : "border-gray-800",
      )}
    />
  );
}

function ImageLabel({ index }: ImageLabelProps) {
  return (
    <div className="absolute bottom-1 left-1 font-mono text-[8px] text-white sm:text-xs">
      IMG278.{index + 1}
    </div>
  );
}

function SectionModuleText() {
  // Implement this component if needed
  return null;
}
